import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/store/auth'

const schema = z.object({
  full_name: z.string().min(2, 'Минимум 2 символа').transform((v) => v.trim()),
  phone: z
    .string()
    .min(7, 'Введите корректный номер')
    .regex(/^\+?[\d\s\-()]{7,20}$/, 'Некорректный формат номера'),
  personal_data_consent: z.literal(true, {
    errorMap: () => ({ message: 'Необходимо дать согласие на обработку персональных данных' }),
  }),
})
type FormData = z.infer<typeof schema>

export default function CompleteProfilePage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const setUser = useAuthStore((s) => s.setUser)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const mutation = useMutation({
    mutationFn: authApi.completeProfile,
    onSuccess: async () => {
      // Обновляем данные пользователя в store
      const res = await authApi.me()
      setUser(res.data)
      navigate('/projects', { replace: true })
    },
  })

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="mb-6 text-center">
          <div className="text-4xl mb-2">👋</div>
          <h1 className="text-xl font-semibold text-gray-900">Добро пожаловать!</h1>
          <p className="mt-1 text-sm text-gray-500">
            Заполните профиль, чтобы начать работу
          </p>
        </div>

        <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-5">
          {/* Email — только для отображения */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={user?.email ?? ''}
              disabled
              className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
            />
          </div>

          {/* ФИО */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ФИО <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Иванов Иван Иванович"
              {...register('full_name')}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-rose-600">{errors.full_name.message}</p>
            )}
          </div>

          {/* Телефон */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Телефон <span className="text-rose-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="+7 (900) 000-00-00"
              {...register('phone')}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-rose-600">{errors.phone.message}</p>
            )}
          </div>

          {/* Согласие 152-ФЗ */}
          <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 space-y-3">
            <p className="text-xs text-gray-600 leading-relaxed">
              В соответствии с Федеральным законом №&nbsp;152-ФЗ «О персональных данных»
              я даю согласие ООО&nbsp;«ГрандГеоПроект» на обработку моих персональных данных
              (ФИО, контактный телефон, адрес электронной почты) в целях обеспечения
              производственной деятельности и управления проектами. Данные хранятся
              на серверах компании и не передаются третьим лицам.
            </p>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                {...register('personal_data_consent')}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">
                Я ознакомлен(а) и согласен(на) на обработку персональных данных
              </span>
            </label>
            {errors.personal_data_consent && (
              <p className="text-xs text-rose-600">{errors.personal_data_consent.message}</p>
            )}
          </div>

          {mutation.isError && (
            <p className="text-sm text-rose-600 text-center">
              Не удалось сохранить профиль. Попробуйте ещё раз.
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || mutation.isPending}
            className="w-full rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Сохранение...' : 'Сохранить и войти'}
          </button>
        </form>
      </div>
    </div>
  )
}
